import {useMemo} from 'react';

import {getEditionStatusInfo} from 'shells/edition/get-edition-status-info';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';
import {
    convertSortByToUrlSortParam,
    convertUrlSortParamToTableSortBy,
} from 'shells/table/table-sort-helpers';
import type {SortBy} from 'shells/table/types';

import type {BroadcastStubFragment_edition as Edition} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';

import styles from './broadcast-stats.module.css';

type Props = {
    isLoading: boolean;
    editions: Edition[];
    sortString: string;
    isFiltering: boolean;
    error: string | null | undefined;
    setIsAddingNewEdition: (arg1: boolean) => void;
    onChangeSort: (newSortString?: string | null | undefined) => void;
    hasFullAccessToCampaigns: boolean;
};

export function BroadcastStats(props: Props) {
    const ownerColumn = useMemo(
        () =>
            props.hasFullAccessToCampaigns
                ? [
                      {
                          id: 'owner',
                          Header: 'Owner',
                          accessor: (edition: Edition) =>
                              edition.campaign.owner ? edition.campaign.owner.name : '',
                          dataType: 'string',
                          shouldTruncate: true,
                          minWidth: 100,
                          width: 150,
                          maxWidth: 200,
                      },
                  ]
                : [],
        [props.hasFullAccessToCampaigns]
    );

    const columns = useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (edition: Edition) => edition,
                dataType: 'linkedName',
            },
            {
                id: 'subject',
                Header: 'Subject',
                accessor: (edition: Edition) =>
                    edition.emailTemplate ? edition.emailTemplate.subject : '',
                dataType: 'string',
                shouldTruncate: true,
                minWidth: 150,
                width: 200,
                maxWidth: 250,
            },
            {
                id: 'sentTime',
                Header: 'Date sent',
                accessor: (edition: Edition) =>
                    edition.sendTime ? edition.sendTime : 'Not yet sent',
                dataType: 'timestamp',
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (edition: Edition) =>
                    getEditionStatusInfo(edition.deletedTime ? 'ARCHIVED' : edition.status),
                dataType: 'statusBadge',
            },
            {
                id: 'from',
                Header: 'Sender',
                accessor: (edition: Edition) =>
                    edition.from ? edition.from.emailAddress.display : '',
                dataType: 'string',
                shouldTruncate: true,
                minWidth: 100,
                width: 150,
                maxWidth: 200,
            },
            ...ownerColumn,
            {
                id: 'audience',
                Header: 'Audience',
                accessor: (edition: Edition) => (edition.audience ? edition.audience.name : ''),
                dataType: 'string',
                shouldTruncate: true,
                minWidth: 150,
                width: 200,
                maxWidth: 250,
            },
            {
                id: 'recipients',
                Header: 'Recipients',
                accessor: (edition: Edition) =>
                    edition.engagementStats.totalSentCount === 0
                        ? '—'
                        : edition.engagementStats.totalSentCount.toLocaleString(),
                dataType: 'string',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'openRate',
                Header: 'Open rate',
                accessor: (edition: Edition) => edition.engagementStats.openRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'clickRate',
                Header: 'Click rate',
                accessor: (edition: Edition) => edition.engagementStats.clickRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'unsubscribeRate',
                Header: 'Unsubscribe rate',
                accessor: (edition: Edition) => edition.engagementStats.unsubscribeRate,
                dataType: 'percentage',
                minWidth: 130,
                width: 130,
                maxWidth: 150,
            },
            {
                id: 'bounceRate',
                Header: 'Bounce rate',
                accessor: (edition: Edition) => edition.engagementStats.permanentBounceRate,
                dataType: 'percentage',
                minWidth: 100,
                width: 100,
                maxWidth: 110,
            },
            {
                id: 'modifiedTime',
                Header: 'Last updated',
                accessor: (edition: Edition) => edition.modifiedTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
                hasRightBorder: true,
            },
        ],
        [ownerColumn]
    );

    const handleSortByChange = (newSortBy: SortBy[]) => {
        const newSortString = convertSortByToUrlSortParam(newSortBy);

        props.onChangeSort(newSortString);
    };

    return (
        <div className={styles['broadcast-stats']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={false}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                sortBy={convertUrlSortParamToTableSortBy(props.sortString)}
                onChangeSortBy={handleSortByChange}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.editions}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            !props.editions.length && props.isFiltering && !props.isLoading,
                        component: (
                            <NothingFoundEmptyState
                                title='No broadcasts were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.editions.length && !props.isLoading,
                        component: (
                            <EmptyState
                                onPrimaryCtaClick={() => props.setIsAddingNewEdition(true)}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
